import { useTranslation } from "react-i18next"

import type { LucideIcon } from "@/components/icons"

import { Briefcase, GraduationCap, Megaphone, Scale, Users } from "@/components/icons"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ensureArray } from "@/features/training/utils/ensure-array"

type UseCase = {
  value: "legal" | "internal" | "marketing" | "technical" | "training"
  icon: LucideIcon
  labelKey: string
  scenarioKey: string
  outcomeKey: string
  examplesKey: string
}

const USE_CASES: ReadonlyArray<UseCase> = [
  {
    value: "legal",
    icon: Scale,
    labelKey: "case_legal_label",
    scenarioKey: "case_legal_scenario",
    outcomeKey: "case_legal_outcome",
    examplesKey: "case_legal_examples",
  },
  {
    value: "internal",
    icon: Users,
    labelKey: "case_internal_label",
    scenarioKey: "case_internal_scenario",
    outcomeKey: "case_internal_outcome",
    examplesKey: "case_internal_examples",
  },
  {
    value: "marketing",
    icon: Megaphone,
    labelKey: "case_marketing_label",
    scenarioKey: "case_marketing_scenario",
    outcomeKey: "case_marketing_outcome",
    examplesKey: "case_marketing_examples",
  },
  {
    value: "technical",
    icon: Briefcase,
    labelKey: "case_technical_label",
    scenarioKey: "case_technical_scenario",
    outcomeKey: "case_technical_outcome",
    examplesKey: "case_technical_examples",
  },
  {
    value: "training",
    icon: GraduationCap,
    labelKey: "case_training_label",
    scenarioKey: "case_training_scenario",
    outcomeKey: "case_training_outcome",
    examplesKey: "case_training_examples",
  },
] as const

export function DemoTranslatorUseCases() {
  const { t } = useTranslation("", { keyPrefix: "training.ai-translator.use_cases" })
  const list = (key: string) => ensureArray<string>(t(key, { returnObjects: true }))

  return (
    <div className="w-full">
      <Tabs className="w-full" defaultValue="legal">
        <TabsList className="w-full flex-wrap justify-start gap-1">
          {USE_CASES.map(({ value, icon: Icon, labelKey }) => (
            <TabsTrigger key={value} value={value}>
              <Icon className="size-4" />
              <span>{t(labelKey)}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {USE_CASES.map(({ value, scenarioKey, outcomeKey, examplesKey }) => (
          <TabsContent key={value} value={value}>
            <div className="bg-background rounded-lg border p-4">
              <p className="text-muted-foreground mb-2 text-[10px] font-bold tracking-wide uppercase">
                {t("scenario_label", "Scenario")}
              </p>
              <p className="mb-4 text-sm leading-relaxed">{t(scenarioKey)}</p>

              <p className="text-primary mb-2 text-[10px] font-bold tracking-wide uppercase">
                {t("outcome_label", "Outcome")}
              </p>
              <p className="mb-4 text-sm leading-relaxed">{t(outcomeKey)}</p>

              <p className="text-muted-foreground mb-2 text-[10px] font-bold tracking-wide uppercase">
                {t("examples_label", "Examples")}
              </p>
              <ul className="space-y-1.5">
                {list(examplesKey).map((example, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="bg-primary/10 text-primary mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-[10px] font-semibold">
                      {i + 1}
                    </span>
                    <span>{example}</span>
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
